import { Injectable, effect, inject, signal } from "@angular/core";
import {
  ActivatedRoute,
  ActivatedRouteSnapshot,
  Router,
} from "@angular/router";

export interface IHandlersRouteCache {
  getMyContextRoute: () => IContextRoute;
  updateMyContextRoute: (updateContextModel: IUpdateContextRoute) => void;
  //backAfterCreate: (key: any) => void;
  backOne: (
    queryParams?: { [key: string]: any },
    data?: any,
    valueForWaitingFormField?: any
  ) => void;
  canICreateNested: () => boolean;
}

export interface IContextRoute {
  scrollPosition: {
    scrollX: number;
    scrollY: number;
  };
  route: string;
  isInit?: boolean;
  data?: any;
  breadcrumb: {
    breadcrumbName?: string;
    isActive: boolean;
  };
  initialPath?: string;
  awaitDataForFieldName?: { dataName: string; fieldName: string };
  canGoBack: boolean;
  queryParams?: { [key: string]: any };
}
export interface IUpdateContextRoute {
  // selectedItem?: number;
  scrollPosition?: {
    scrollX: number;
    scrollY: number;
  };
  data?: any;
  awaitDataForFieldName?: { dataName: string; fieldName: string };
  queryParams?: { [key: string]: any };
}

@Injectable({
  providedIn: "root",
})
export class RouteCacheService {
  private readonly router = inject(Router);
  private contextRoutesList = signal<IContextRoute[]>([]);

  constructor() {
    this.contextRoutesList.set(
      JSON.parse(
        sessionStorage.getItem("routeCache") ?? "[]"
      ) as IContextRoute[]
    );

    effect(() => {
      const contextList = this.contextRoutesList();
      sessionStorage.setItem("routeCache", JSON.stringify(contextList));
    });
  }

  private getListContext() {
    return this.contextRoutesList;
  }

  public toolsBreadcrumb() {
    return {
      getListContext: () => this.getListContext(),
      onBack: (queryParams?: { [key: string]: any }) =>
        this.backOne(queryParams),
    };
  }

  public createContextRoute(route: ActivatedRoute) {
    const myContext = this.getMyContextRoute(route);
    const queryParams = route.snapshot.queryParams;
    const data = myContext ? myContext.data : undefined;
    const scrollPosition =
      myContext && myContext.scrollPosition
        ? myContext.scrollPosition
        : { scrollX: 0, scrollY: 0 };

    if (myContext) {
      const pos = this.contextRoutesList().findIndex(
        (c) => c.route == route.snapshot.url.join("/")
      );

      if (myContext.isInit) {
        this.clearContextRoutesList();
        myContext.breadcrumb.isActive = true;
        myContext.awaitDataForFieldName = undefined;
        myContext.canGoBack = false;
        myContext.queryParams = { ...queryParams };
        myContext.data = data;
        myContext.scrollPosition = scrollPosition;
        myContext.breadcrumb.breadcrumbName = route.snapshot.data["breadcrumb"];
        this.contextRoutesList.update((cL) => cL.concat(myContext));
      } else {
        this.contextRoutesList.update((cl) => {
          cl.splice(pos + 1);
          cl[pos].breadcrumb.isActive = true;
          cl[pos].awaitDataForFieldName = undefined;
          cl[pos].queryParams = {
            ...cl[pos].queryParams,
            ...queryParams,
          };
          return cl;
        });
        sessionStorage.setItem(
          "routeCache",
          JSON.stringify(this.contextRoutesList())
        );
      }
    } else {
      if (
        route.snapshot.data["mode"] == "browser" &&
        route.snapshot.data["initialPath"] !==
          this.contextRoutesList()[0]?.initialPath &&
        route.snapshot.data["initialPath"] !== route.snapshot.url.join("/")
      ) {
        const initialContext: IContextRoute = {
          breadcrumb: {
            isActive: false,
            breadcrumbName: route.snapshot.data["initialBreadcrumbName"],
          },
          canGoBack: false,
          route: route.snapshot.data["initialPath"],
          scrollPosition: {
            scrollX: 0,
            scrollY: 0,
          },
          isInit: true,
          initialPath: route.snapshot.data["initialPath"],
        };
        this.contextRoutesList.set([
          initialContext,
          {
            scrollPosition,
            route: route.snapshot.url.join("/"),
            breadcrumb: {
              breadcrumbName: route.snapshot.data["breadcrumb"],
              isActive: true,
            },
            isInit: false,
            initialPath: route.snapshot.data["initialPath"] ?? "/",
            awaitDataForFieldName: undefined,
            canGoBack: true,
            queryParams: queryParams,
          },
        ]);
      } else {
        this.contextRoutesList.update((cl) => {
          const contexts = cl;
          for (let index = 0; index < contexts.length; index++) {
            contexts[index].breadcrumb.isActive = false;
          }
          return cl.concat({
            scrollPosition,
            route: route.snapshot.url.join("/"),
            breadcrumb: {
              breadcrumbName: route.snapshot.data["breadcrumb"],
              isActive: true,
            },
            isInit:
              route.snapshot.data["initialPath"] ==
              route.snapshot.url.join("/"),
            initialPath: route.snapshot.data["initialPath"] ?? "/",
            awaitDataForFieldName: undefined,
            canGoBack: true,
            queryParams: queryParams,
          });
        });
        if (
          route.snapshot.data["initialPath"] == route.snapshot.url.join("/")
        ) {
          this.createContextRoute(route);
        }
      }
    }

    return {
      getMyContextRoute: () => this.getMyContextRoute(route),
      updateMyContextRoute: (updateContextModel: IUpdateContextRoute) =>
        this.updateMyContextRoute(updateContextModel),
      //backAfterCreate: (key: any) => this.backAfterCreate(key),
      backOne: (
        queryParams?: { [key: string]: any },
        data?: any,
        valueForWaitingFormField?: any
      ) => this.backOne(queryParams, data, valueForWaitingFormField),
      canICreateNested: () => this.canICreateNested(),
    } as IHandlersRouteCache;
  }
  private canICreateNested() {
    const canCreateNested =
      this.contextRoutesList().find((c) => c.awaitDataForFieldName) ==
      undefined;
    return canCreateNested;
  }
  public backOne(
    queryParams?: { [key: string]: any },
    data?: any,
    valueForWaitingFormField?: any
  ) {
    const context =
      this.contextRoutesList()[this.contextRoutesList().length - 2];
    if (context) {
      if (valueForWaitingFormField) {
        if (context.awaitDataForFieldName) {
          context.data[context.awaitDataForFieldName.dataName!][
            context.awaitDataForFieldName.fieldName
          ] = valueForWaitingFormField;
          this.contextRoutesList.update((cL) =>
            cL.map((c) =>
              c.route == context.route
                ? { ...context, awaitDataForFieldName: undefined }
                : c
            )
          );
        }
      }

      // Mergear data si se proporciona
      if (data) {
        context.data = { ...context.data, ...data };
        this.contextRoutesList.update((cL) =>
          cL.map((c) => (c.route == context.route ? { ...context } : c))
        );
      }

      this.router.navigate([context.route], {
        queryParams: { ...context.queryParams, ...queryParams },
      });
    } else {
      try {
        this.router.navigate([
          this.contextRoutesList()[this.contextRoutesList().length - 1]
            .initialPath,
        ]);
      } catch {
        this.router.navigate(["/"]);
      }
    }
  }
  // private backAfterCreate(key: any) {
  //   const context =
  //     this.contextRoutesList()[this.contextRoutesList().length - 2];
  //   if (context) {
  //     if (context.awaitDataForFieldName) {
  //       context.data[context.awaitDataForFieldName!] = key;
  //       this.contextRoutesList.update((cL) =>
  //         cL.map((c) =>
  //           c.route == context.route
  //             ? { ...context, awaitDataForFieldName: undefined }
  //             : c
  //         )
  //       );
  //     }
  //     // else {
  //     //   this.contextRoutesList.update((cl) =>
  //     //     cl.map((c) => (c.route == context.route ? { ...context } : c))
  //     //   );
  //     // }
  //     this.router.navigate([
  //       this.contextRoutesList()[this.contextRoutesList().length - 2].route,
  //     ]);
  //   } else {
  //     try {
  //       this.router.navigate([
  //         this.contextRoutesList()[this.contextRoutesList().length - 1]
  //           .initialPath,
  //       ]);
  //     } catch {
  //       this.router.navigate(['/']);
  //     }
  //   }
  // }
  private updateMyContextRoute(updateContextModel: IUpdateContextRoute) {
    const context =
      this.contextRoutesList()[this.contextRoutesList().length - 1];
    if (context) {
      const { data, awaitDataForFieldName, queryParams, scrollPosition } =
        updateContextModel;
      this.contextRoutesList.update((cL) =>
        cL.map((c) =>
          c.route == context.route
            ? {
                ...context,
                data,
                awaitDataForFieldName,
                queryParams,
                scrollPosition: scrollPosition ?? { scrollX: 0, scrollY: 0 },
              }
            : c
        )
      );
    } else {
      throw new Error("Error al Actualizar contexto.");
    }
  }
  private getMyContextRoute(route: ActivatedRoute) {
    return this.contextRoutesList().find(
      (c) => c.route === route.snapshot.url.join("/")
    ) as IContextRoute;
  }

  getContextRoute(route: ActivatedRouteSnapshot) {
    return this.contextRoutesList().find(
      (c) => c.route === route.url.join("/")
    ) as IContextRoute;
  }

  // tools
  private clearContextRoutesList() {
    this.contextRoutesList.set([]);
  }
}
